// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { Sort } from "@angular/material/sort";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

// Core packages
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";

import { AuthService } from "src/app/modules/auth/auth.service";
import { DdtsService } from "src/app/modules/ddts/ddts.service";
import { RolesService } from "src/app/modules/roles/roles.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-checklist-list",
  templateUrl: "./checklist-list.page.html",
  styleUrls: ["./checklist-list.page.scss"],
})
export class ChecklistListComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  loggedUserMaster = false;
  dateTimeFormat: DateTimeFormatPipe;
  activeSection: string; // Active document section
  activeSectionData: any[] = [];
  itemId: string; // Id of active mapping section item
  condoStructure: any;
  documentArchive: any;
  documentArchiveId: string;
  sortedDocuments: any[] = [];
  documentArchiveIsLoading = true;

  checklist: any[] = [];
  sortedData = [];

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private propertiesService: PropertiesService,
    private router: Router,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private ddtsService: DdtsService,
    private rolesService: RolesService,
    private helperService: HelperService
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get logged user's role data
    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });
    this.loggedUserRole = this.currentRole.role;
    this.loggedUserMaster =
      this.loggedUserRole.type === "admin" ||
      this.loggedUserRole.type === "operator";
    // this.subscriptions.push(
    //   this.rolesService
    //     .get(this.authService.loggedUserRequestedRole$.getValue())
    //     .subscribe(res2 => {
    //       if (res2.status) {
    //         this.loggedUserRole = res2.role;
    //         this.loggedUserMaster =
    //           this.loggedUserRole.type === "master" ||
    //           this.loggedUserRole.type === "collaborator";
    //       }
    //     })
    // );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.subscriptions.push(
      this.ddtsService.getChecklist().subscribe((res) => {
        if (res.status) {
          if (res.data && Array.isArray(res.data)) {
            res.data.forEach((item: any) => {
              const index = this.checklist.findIndex((el: any) => {
                return item.ddt.code === el.ddtCode;
              });
              if (index > -1) {
                let file = 0;
                let docs = 0;
                if (item.files && Array.isArray(item.files)) {
                  item.files.forEach((el: any) => {
                    if (el.status === "missing") {
                      file++;
                    }
                    if (!el.invoiced) {
                      docs++;
                    }
                  });
                }
                this.checklist[index].file += file;
                this.checklist[index].docs += docs;
              } else {
                let file = 0;
                let docs = 0;
                if (item.files && Array.isArray(item.files)) {
                  item.files.forEach((el: any) => {
                    if (el.status === "missing") {
                      file++;
                    }
                    if (!el.invoiced) {
                      docs++;
                    }
                  });
                }
                this.checklist.push({
                  ddtId: item.ddt._id,
                  ddtCode: item.ddt.code,
                  file: file,
                  docs: docs,
                });
              }
            });
          }
          this.sortedData = this.checklist.slice();

          this.documentArchiveIsLoading = false;
        } else {
          this.documentArchiveIsLoading = false;
          this.toastrService.error(res.message);
        }
      })
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortData(sort: Sort): any {
    const data = this.sortedData;
    if (!sort.active || sort.direction === "") {
      this.sortedData = data;
      return;
    }

    this.sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === "asc";
      switch (sort.active) {
        case "ddtCode":
          return this.compare(a.ddtCode, b.ddtCode, isAsc);
        case "file":
          return this.compare(a.file, b.file, isAsc);
        case "docs":
          return this.compare(a.docs, b.docs, isAsc);
        default:
          return 0;
      }
    });
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(a: number | string, b: number | string, isAsc: boolean): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
